import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { PageHeader, Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { StatCard } from "../components/ui/StatCard";
import { TableCard, td, tdMuted, trStyle } from "../components/ui/Table";
import { EmptyState, LoadingRow } from "../components/ui/EmptyState";
import { colors, radius } from "../components/ui/tokens";
import { CheckIcon, EyeIcon, XIcon } from "../components/ui/icons";

const TemplateIcon = ({ type, color }: { type: string; color: string }) => {
  const s = { width: 20, height: 20, fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  if (type === "welcome") return <svg viewBox="0 0 24 24" {...s}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
  if (type === "reset-password") return <svg viewBox="0 0 24 24" {...s}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
  if (type === "two-factor") return <svg viewBox="0 0 24 24" {...s}><rect x="5" y="11" width="14" height="10" rx="2"/><circle cx="12" cy="16" r="1.5" fill={color}/><path d="M8 11V7a4 4 0 0 1 8 0"/></svg>;
  if (type === "booking-confirmation") return <svg viewBox="0 0 24 24" {...s}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 15 11 17 15 13"/></svg>;
  if (type === "offer-accepted") return <svg viewBox="0 0 24 24" {...s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 15 11 17 15 12.5"/></svg>;
  if (type === "purchase-receipt") return <svg viewBox="0 0 24 24" {...s}><path d="M6 2h12v18l-3-2-3 2-3-2-3 2z"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="9" y1="12" x2="15" y2="12"/></svg>;
  if (type === "payout-released") return <svg viewBox="0 0 24 24" {...s}><path d="M18 6.5A6.5 6.5 0 0 0 8 13"/><path d="M8 13a6.5 6.5 0 0 0 10 5.5"/><line x1="3" y1="10" x2="14" y2="10"/><line x1="3" y1="15" x2="12" y2="15"/></svg>;
  if (type === "payment-refunded") return <svg viewBox="0 0 24 24" {...s}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-8.36L1 10"/></svg>;
  if (type === "payment-disputed") return <svg viewBox="0 0 24 24" {...s}><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
  return <svg viewBox="0 0 24 24" {...s}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
};
const SendIcon = ({ size = 16, color = "currentColor" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
);
const DocIcon = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;

const TEMPLATE_META: Record<string, { name: string; desc: string; color: string }> = {
  "welcome": { name: "Bienvenue", desc: "Envoyé à l'inscription", color: colors.accent },
  "two-factor": { name: "Code 2FA", desc: "Envoyé à chaque connexion (si activé)", color: "#534AB7" },
  "booking-confirmation": { name: "Confirmation de réservation", desc: "Envoyé au client à la réservation d'un lieu", color: colors.success },
  "reset-password": { name: "Réinitialisation MDP", desc: "Lien de réinitialisation", color: colors.danger },
  // Traces écrites financières/contractuelles (12 langues côté templates, voir utils/email.ts) —
  // ajoutées en même temps que les 5 nouveaux emails eux-mêmes, sans quoi ils resteraient
  // invisibles ici : absents de la galerie, impossibles à tester, exclus des totaux ci-dessous
  // (calculés uniquement à partir des clés de ce dictionnaire).
  "offer-accepted": { name: "Devis accepté", desc: "Envoyé au prestataire dont le devis Power est accepté", color: colors.success },
  "purchase-receipt": { name: "Reçu d'achat", desc: "Envoyé à l'acheteur après un paiement réussi", color: colors.accent },
  "payout-released": { name: "Virement effectué", desc: "Envoyé au vendeur/prestataire quand l'argent est viré", color: colors.success },
  "payment-refunded": { name: "Remboursement", desc: "Envoyé à l'acheteur en cas de remboursement", color: colors.warning },
  "payment-disputed": { name: "Paiement en vérification", desc: "Envoyé quand un paiement est signalé ou mis en attente", color: colors.danger },
};

// Sélecteur de langue — texte plutôt que des drapeaux emoji (jamais d'émoji dans ce panel, voir
// components/ui/icons.tsx), un badge par langue réellement testable ici (les 12 langues de l'app
// existent côté templates, mais seuls FR/EN ont un test manuel utile pour l'instant).
const LANGS = ["fr", "en"];
const LANG_LABEL: Record<string, string> = { fr: "FR", en: "EN" };

const TABS = [
  { key: "templates", label: "Templates" },
  { key: "send", label: "Envoyer un test" },
  { key: "logs", label: "Historique" },
  { key: "settings", label: "Configuration" },
];

export default function Emails() {
  const [activeTab, setActiveTab] = useState<"templates" | "send" | "logs" | "settings">("templates");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState("fr");
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState("");

  const [stats, setStats] = useState<any[]>([]);
  const [recent, setRecent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/admin/emails").then(data => {
      if (data?.stats) setStats(data.stats);
      if (data?.recent) setRecent(data.recent);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const statFor = (template: string) => stats.find(s => s.template === template) || { sent_count: 0, delivered_count: 0, opened_count: 0, bounced_count: 0 };
  const templates = Object.keys(TEMPLATE_META).map(id => {
    const s = statFor(id);
    const openRate = s.delivered_count > 0 ? Math.round((s.opened_count / s.delivered_count) * 100) : 0;
    return { id, ...TEMPLATE_META[id], sent: Number(s.sent_count), openRate };
  });

  const totalSent = templates.reduce((a, t) => a + t.sent, 0);
  const totalOpened = templates.reduce((a, t) => a + Number(statFor(t.id).opened_count), 0);
  const totalDelivered = templates.reduce((a, t) => a + Number(statFor(t.id).delivered_count), 0);
  const avgOpen = totalDelivered > 0 ? Math.round((totalOpened / totalDelivered) * 100) : 0;

  const handleSendTest = () => {
    if (!testEmail || !selectedTemplate) return;
    setSending(true);
    setSendError("");
    api.post("/admin/emails/test", { template: selectedTemplate, to: testEmail, lang: selectedLang })
      .then(data => {
        if (data?.message) { setSent(true); setTimeout(() => setSent(false), 3000); load(); }
        else setSendError(data?.error || "Erreur lors de l'envoi");
      })
      .catch(() => setSendError("Erreur de connexion"))
      .finally(() => setSending(false));
  };

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Emails" subtitle={`${templates.length} templates transactionnels · suivi via webhooks Resend`} />

      <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <StatCard label="Emails envoyés" value={totalSent.toLocaleString()} tone="accent" icon={<SendIcon color={colors.accent} />} />
        <StatCard label="Taux d'ouverture" value={`${avgOpen}%`} tone="success" icon={<EyeIcon size={18} color={colors.success} />} />
        <StatCard label="Templates actifs" value={templates.length} tone="warning" icon={<DocIcon color={colors.warning} />} />
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 20, backgroundColor: colors.card, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: 4 }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as any)} style={{
            flex: 1, padding: "10px", borderRadius: radius.sm, border: "none", cursor: "pointer",
            backgroundColor: activeTab === tab.key ? colors.text : "transparent",
            fontWeight: activeTab === tab.key ? 700 : 500,
            fontSize: 13, color: activeTab === tab.key ? "white" : colors.textMuted,
          }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === "templates" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          {templates.map(template => (
            <Card key={template.id} style={{ cursor: "pointer", border: `1px solid ${selectedTemplate === template.id ? template.color : colors.border}` }}>
              <div onClick={() => setSelectedTemplate(selectedTemplate === template.id ? null : template.id)}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: radius.md, backgroundColor: template.color + "1A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><TemplateIcon type={template.id} color={template.color} /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: colors.text, marginBottom: 2 }}>{template.name}</div>
                    <div style={{ fontSize: 12, color: colors.textMuted }}>{template.desc}</div>
                  </div>
                  <Badge tone="success" dot>Actif</Badge>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1, backgroundColor: colors.bg, borderRadius: radius.sm, padding: "8px 12px" }}>
                    <div style={{ fontSize: 11, color: colors.textFaint, marginBottom: 2 }}>Envoyés</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: template.color }}>{template.sent.toLocaleString()}</div>
                  </div>
                  <div style={{ flex: 1, backgroundColor: colors.bg, borderRadius: radius.sm, padding: "8px 12px" }}>
                    <div style={{ fontSize: 11, color: colors.textFaint, marginBottom: 2 }}>Ouverture</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: template.openRate > 75 ? colors.success : template.openRate > 50 ? colors.warning : colors.danger }}>{template.openRate}%</div>
                  </div>
                </div>
              </div>
              {selectedTemplate === template.id && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${colors.border}` }}>
                  <Button variant="primary" style={{ width: "100%", justifyContent: "center", backgroundColor: template.color }} onClick={() => setActiveTab("send")}><SendIcon size={14} /> Tester ce template</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {activeTab === "send" && (
        <div style={{ maxWidth: 560 }}>
          <Card>
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.text, marginBottom: 20 }}>Envoyer un email de test</div>

            <label style={{ fontSize: 12.5, fontWeight: 700, display: "block", marginBottom: 8, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>Template</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 20 }}>
              {templates.map(t => (
                <div key={t.id} onClick={() => setSelectedTemplate(t.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: radius.sm, border: `1px solid ${selectedTemplate === t.id ? t.color : colors.borderStrong}`, backgroundColor: selectedTemplate === t.id ? t.color + "10" : "white", cursor: "pointer" }}>
                  <TemplateIcon type={t.id} color={t.color} />
                  <span style={{ fontSize: 13, fontWeight: selectedTemplate === t.id ? 700 : 400, color: selectedTemplate === t.id ? t.color : colors.textMuted }}>{t.name}</span>
                </div>
              ))}
            </div>

            <label style={{ fontSize: 12.5, fontWeight: 700, display: "block", marginBottom: 8, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>Langue</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
              {LANGS.map(l => (
                <button key={l} onClick={() => setSelectedLang(l)} style={{ padding: "6px 14px", borderRadius: radius.pill, border: `1px solid ${selectedLang === l ? colors.text : colors.borderStrong}`, backgroundColor: selectedLang === l ? colors.text : "white", color: selectedLang === l ? "white" : colors.textMuted, cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}>
                  {LANG_LABEL[l]}
                </button>
              ))}
            </div>

            <label style={{ fontSize: 12.5, fontWeight: 700, display: "block", marginBottom: 8, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>Email destinataire</label>
            <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="toi@exemple.com" style={{ width: "100%", padding: "12px 16px", borderRadius: radius.sm, border: `1px solid ${colors.borderStrong}`, fontSize: 14, outline: "none", marginBottom: 20, boxSizing: "border-box" }} />

            {sent && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, backgroundColor: colors.successSoft, borderRadius: radius.sm, padding: 14, marginBottom: 16, fontSize: 13.5, color: colors.success, fontWeight: 600 }}>
                <CheckIcon size={16} color={colors.success} /> Email de test envoyé avec succès !
              </div>
            )}
            {sendError && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, backgroundColor: colors.dangerSoft, borderRadius: radius.sm, padding: 14, marginBottom: 16, fontSize: 13.5, color: colors.danger, fontWeight: 600 }}>
                <XIcon size={16} color={colors.danger} /> {sendError}
              </div>
            )}

            <Button variant="primary" disabled={!testEmail || !selectedTemplate || sending} style={{ width: "100%", justifyContent: "center", padding: 14, fontSize: 15 }} onClick={handleSendTest}>
              <SendIcon size={15} /> {sending ? "Envoi en cours..." : "Envoyer le test"}
            </Button>
          </Card>
        </div>
      )}

      {activeTab === "logs" && (
        <TableCard headers={["Template", "Destinataire", "Langue", "Statut", "Date"]}>
          {loading ? (
            <tr><td colSpan={5}><LoadingRow /></td></tr>
          ) : recent.length === 0 ? (
            <tr><td colSpan={5}><EmptyState title="Aucun envoi pour le moment" /></td></tr>
          ) : recent.map(send => {
            const status = send.bounced_at ? "bounced" : send.opened_at ? "opened" : send.status === "failed" ? "failed" : "delivered";
            const tone = status === "delivered" ? "success" : status === "opened" ? "accent" : "danger";
            const label = status === "delivered" ? "Délivré" : status === "opened" ? "Ouvert" : status === "bounced" ? "Rejeté" : "Échec";
            return (
              <tr key={send.id} style={trStyle}>
                <td style={{ ...td, fontWeight: 600 }}>{TEMPLATE_META[send.template]?.name || send.template}</td>
                <td style={tdMuted}>{send.recipient}</td>
                <td style={td}>{LANG_LABEL[send.lang] || send.lang}</td>
                <td style={td}><Badge tone={tone}>{label}</Badge></td>
                <td style={{ ...td, color: colors.textFaint, fontSize: 12.5 }}>{new Date(send.created_at).toLocaleString("fr-FR")}</td>
              </tr>
            );
          })}
        </TableCard>
      )}

      {activeTab === "settings" && (
        <div style={{ maxWidth: 560 }}>
          <Card>
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.text, marginBottom: 6 }}>Configuration</div>
            <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 20 }}>Ces valeurs sont définies sur le serveur (variables d'environnement) et ne sont pas modifiables depuis ce panel.</p>
            {[
              { label: "Service d'envoi", value: "Resend" },
              { label: "Email expéditeur", value: "contact@oltodo.com" },
              { label: "Nom expéditeur", value: "OLTODO" },
            ].map((field, i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12.5, fontWeight: 700, display: "block", marginBottom: 6, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>{field.label}</label>
                <div style={{ width: "100%", padding: "11px 14px", borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: 14, backgroundColor: colors.bg, color: colors.textMuted, boxSizing: "border-box" }}>{field.value}</div>
              </div>
            ))}
            <div style={{ backgroundColor: colors.warningSoft, borderRadius: radius.sm, padding: 14, fontSize: 12.5, color: colors.warning, marginTop: 8 }}>
              Pour activer le suivi des ouvertures/clics, configure un webhook dans le dashboard Resend pointant vers <code>https://api.oltodo.com/api/webhooks/resend</code>, puis renseigne <code>RESEND_WEBHOOK_SECRET</code> sur le serveur.
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
